import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Brain, FileText, Loader2, MessageSquareReply, Pill, RefreshCw, ShieldAlert, Smartphone, Sparkles, Stethoscope } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';

type PatientAiSummary = {
  patientId: string;
  summaryText: string;
  highlights: string[];
  riskFlags: string[];
  careGaps: string[];
  generatedAt: string;
  sourceSignature: string;
  fromCache: boolean;
};

function formatDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function SmallList({ title, items, icon }: { title: string; items: string[]; icon: ReactNode }) {
  const { tr } = useI18n();
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        {icon}
        {tr(title)}
      </div>
      {items.length ? (
        <ul className="text-sm text-slate-700 space-y-1">
          {items.map((item, idx) => (
            <li key={`${title}-${idx}`}>• {item}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">{tr('None flagged')}</p>
      )}
    </div>
  );
}

export function PatientAiSummaryCard({ patientId }: { patientId: string }) {
  const { fetchJson } = useApiClient();
  const { tr, language } = useI18n();
  const [summary, setSummary] = useState<PatientAiSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentOutput, setAgentOutput] = useState('Echo AI client summary panel is ready.');

  const loadSummary = useCallback(
    async (force?: boolean) => {
      if (force) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('language', language);
        if (force) params.set('force', 'true');
        const path = `/patients/${patientId}/ai-summary?${params.toString()}`;
        const data = await fetchJson<PatientAiSummary>(path);
        setSummary(data);
      } catch (err: any) {
        setError(err.message || tr('Unable to generate patient AI summary.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchJson, language, patientId, tr],
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-sky-700">{tr('AI Summary')}</p>
          <h3 className="text-base font-semibold text-sky-900 flex items-center gap-2">
            <Brain size={16} />
            {tr('Longitudinal Clinical Snapshot')}
          </h3>
          <p className="text-xs text-sky-700">
            {tr('Generated')} {summary ? formatDate(summary.generatedAt) : '-'} {summary?.fromCache ? `• ${tr('cached')}` : ''}
          </p>
        </div>
        <button
          onClick={() => loadSummary(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-sky-300 bg-white text-sky-900 text-xs hover:bg-sky-100 disabled:opacity-60"
          title={tr('Rebuild summary now')}
        >
          {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {tr('Refresh')}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-sky-700 inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {tr('Building chart summary...')}
        </div>
      ) : error ? (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded px-3 py-2">{error}</div>
      ) : summary ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-800 leading-relaxed">{summary.summaryText}</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <SmallList title={tr('Highlights')} items={summary.highlights} icon={<Stethoscope size={12} />} />
            <SmallList title={tr('Risk Flags')} items={summary.riskFlags} icon={<ShieldAlert size={12} />} />
            <SmallList title={tr('Care Gaps')} items={summary.careGaps} icon={<Brain size={12} />} />
          </div>
          <div className="rounded-lg border border-sky-200 bg-white p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-sky-800">
              <Sparkles size={13} /> {tr('Echo AI Agents')}
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <AgentButton icon={<Pill size={13} />} label={tr('Drug agent')} onClick={() => setAgentOutput(tr('Prescription drug-agent draft: check allergies, interactions, controlled-drug rules, and dosage instructions.'))} />
              <AgentButton icon={<FileText size={13} />} label={tr('Letter agent')} onClick={() => setAgentOutput(tr('Referral letter draft created from the current client summary and care gaps.'))} />
              <AgentButton icon={<Stethoscope size={13} />} label={tr('Service copy')} onClick={() => setAgentOutput(tr('Service-description content draft prepared for online booking and aftercare.'))} />
              <AgentButton icon={<MessageSquareReply size={13} />} label={tr('Review reply')} onClick={() => setAgentOutput(tr('Review-response draft prepared with a compliant, friendly tone.'))} />
            </div>
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900">
              <Smartphone size={14} className="mt-0.5 shrink-0" />
              <span>{agentOutput} {tr('Mobile journey: outputs are prepared for staff review in Pabau Go style workflows.')}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-sky-700">{tr('No summary available yet.')}</div>
      )}
    </div>
  );
}

function AgentButton({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center justify-center gap-1 rounded-lg border border-sky-200 bg-white px-2 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-50">
      {icon}
      {label}
    </button>
  );
}
